import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiPackage,
  FiPrinter,
  FiTrendingUp,
  FiAlertTriangle,
  FiLayers,
  FiArchive,
  FiRefreshCcw,
  FiSearch,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';
import ClientPagination, { useClientPagination } from '../../components/common/ClientPagination';

const COLORS = ["#7A2238", "#D4AF37", "#EF4444", "#2563EB", "#A94D6D"];

function indiaDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function number(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function comparison(value, inverse = false) {
  if (value === null || value === undefined) {
    return { text: "No data last year", color: "bg-gray-100 text-gray-500" };
  }

  const amount = Number(value);
  const good = inverse ? amount <= 0 : amount >= 0;

  return {
    text: `${amount >= 0 ? "+" : ""}${amount.toFixed(1)}% vs last year`,
    color: good ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700",
  };
}

function chartLabel(date) {
  if (!date) return "";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(new Date(`${date}T00:00:00.000Z`));
}

export default function InventoryReports() {
  const { accessToken } = useAuth();
  const today = indiaDate();

  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [storeId, setStoreId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [search, setSearch] = useState("");

  const [report, setReport] = useState(null);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadFilters = useCallback(async () => {
    if (!accessToken) return;

    try {
      const [storeData, brandData, categoryData] = await Promise.all([
        apiRequest("/stores", { accessToken }),
        apiRequest("/brands", { accessToken }),
        apiRequest("/categories", { accessToken }),
      ]);

      setStores(storeData || []);
      setBrands(brandData || []);
      setCategories(categoryData || []);
    } catch {
      // Filters are optional; report still loads.
    }
  }, [accessToken]);

  const loadReport = useCallback(
    async (refresh = false) => {
      if (!accessToken) return;

      try {
        refresh ? setRefreshing(true) : setLoading(true);
        setError("");

        const query = new URLSearchParams({
          from: dateFrom,
          to: dateTo,
        });

        if (storeId) query.set("storeId", storeId);
        if (brandId) query.set("brandId", brandId);
        if (categoryId) query.set("categoryId", categoryId);

        const response = await apiRequest(
          `/reports/inventory?${query.toString()}`,
          { accessToken },
        );

        setReport(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load inventory report.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accessToken, dateFrom, dateTo, storeId, brandId, categoryId],
  );

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const inventory = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (report?.inventory || []).filter((item) => {
      if (!query) return true;

      return (
        item.product?.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query) ||
        item.barcode?.toLowerCase().includes(query)
      );
    });
  }, [report?.inventory, search]);

  const stockMovement = useMemo(() => {
    const previous = report?.previousStockMovement || [];

    return (report?.stockMovement || []).map((item, index) => ({
      label: chartLabel(item.date),
      current: Number(item.quantity || 0),
      previous: Number(previous[index]?.quantity || 0),
    }));
  }, [report]);

  const metrics = report?.metrics || {};
  const changes = report?.comparison?.changes || {};

  const cards = [
    {
      title: "Total Products",
      value: number(metrics.totalProducts),
      compare: comparison(changes.totalProducts),
      icon: <FiPackage size={24} />,
      color: "bg-blue-50 text-blue-600",
    },
    {
      title: "Available Stock",
      value: number(metrics.availableStock),
      compare: comparison(changes.availableStock),
      icon: <FiArchive size={24} />,
      color: "bg-green-50 text-green-600",
    },
    {
      title: "Low Stock",
      value: number(metrics.lowStock),
      compare: comparison(changes.lowStock, true),
      icon: <FiAlertTriangle size={24} />,
      color: "bg-yellow-50 text-yellow-700",
    },
    {
      title: "Out Of Stock",
      value: number(metrics.outOfStock),
      compare: comparison(changes.outOfStock, true),
      icon: <FiAlertTriangle size={24} />,
      color: "bg-red-50 text-red-600",
    },
    {
      title: "Stock Value",
      value: money(metrics.stockValue),
      compare: comparison(changes.stockValue),
      icon: <FiTrendingUp size={24} />,
      color: "bg-purple-50 text-purple-700",
    },
    {
      title: "Categories",
      value: number(metrics.categories),
      compare: { text: "Current inventory", color: "bg-gray-100 text-gray-500" },
      icon: <FiLayers size={24} />,
      color: "bg-pink-50 text-pink-600",
    },
  ];


  const pagination = useClientPagination(inventory);
  useEffect(() => { pagination.setPage(1); }, [search]);
  return (
    <div className="charms-analytics space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-800">
            Inventory Analytics
          </h1>
          <p className="mt-2 text-gray-500">
            Live stock movement, valuation, availability and last-year comparison.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="rounded-xl border bg-white px-5 py-3"
          >
            <FiPrinter />
          </button>

          <button
            onClick={() => loadReport(true)}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-xl border bg-white px-5 py-3 disabled:opacity-50"
          >
            <FiRefreshCcw className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(event) => setDateFrom(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          />

          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={today}
            onChange={(event) => setDateTo(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          />

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Stores</option>
            {stores
              .filter((store) => store.isActive)
              .map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name} ({store.code})
                </option>
              ))}
          </select>

          <select
            value={brandId}
            onChange={(event) => setBrandId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Brands</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>

          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500">
          Loading inventory report...
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card) => (
              <div key={card.title} className="analytics-kpi">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{card.title}</p>
                    <h3 className="mt-2 text-3xl font-bold">{card.value}</h3>
                    <span
                      className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-semibold ${card.compare.color}`}
                    >
                      {card.compare.text}
                    </span>
                  </div>

                  <div className="analytics-icon flex items-center justify-center">
                    {card.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-2 text-xl font-bold">Stock Movement Comparison</h2>
              <p className="mb-5 text-sm text-gray-500">
                Net movement in the selected period versus last year.
              </p>

              {stockMovement.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={stockMovement}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="current"
                      name="This Year"
                      stroke="#7A2238"
                      strokeWidth={3}
                    />
                    <Line
                      type="monotone"
                      dataKey="previous"
                      name="Last Year"
                      stroke="#D4AF37"
                      strokeWidth={3}
                      strokeDasharray="7 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[320px] items-center justify-center text-gray-400">
                  No stock movement during this period.
                </div>
              )}
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-xl font-bold">Stock Status</h2>

              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={report.stockStatus || []}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                  >
                    {(report.stockStatus || []).map((item, index) => (
                      <Cell
                        key={item.name}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </section>
          </div>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-xl font-bold">Category Wise Stock</h2>

            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={report.categoryStock || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="qty" fill="#7A2238" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold">Inventory Report</h2>
                <p className="text-gray-500">Product-wise inventory summary</p>
              </div>

              <div className="relative w-full lg:w-80">
                <FiSearch className="absolute left-4 top-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search Product / SKU / Barcode"
                  className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[1600px]">
                <thead className="bg-[#F8F6F5]">
                  <tr>
                    {[
                      "Product",
                      "SKU",
                      "Brand",
                      "Category",
                      "Opening",
                      "Purchased",
                      "Sold",
                      "Adjusted",
                      "Closing",
                      "Available",
                      "Cost",
                      "Stock Value",
                      "Status",
                    ].map((heading) => (
                      <th key={heading} className="px-4 py-3 text-left">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {pagination.pageItems.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="font-semibold">{item.product}</p>
                        <p className="text-sm text-gray-500">
                          {item.color} • {item.size}
                        </p>
                      </td>
                      <td className="px-4 py-4">{item.sku}</td>
                      <td className="px-4 py-4">{item.brand}</td>
                      <td className="px-4 py-4">{item.category}</td>
                      <td className="px-4 py-4 text-center">{number(item.opening)}</td>
                      <td className="px-4 py-4 text-center text-blue-600">{number(item.purchased)}</td>
                      <td className="px-4 py-4 text-center text-red-600">{number(item.sold)}</td>
                      <td className="px-4 py-4 text-center">{number(item.adjusted)}</td>
                      <td className="px-4 py-4 text-center">{number(item.closing)}</td>
                      <td className="px-4 py-4 text-center font-bold text-green-600">{number(item.available)}</td>
                      <td className="px-4 py-4 text-right">{money(item.cost)}</td>
                      <td className="px-4 py-4 text-right font-semibold">{money(item.value)}</td>
                      <td className="px-4 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            item.status === "In Stock"
                              ? "bg-green-100 text-green-700"
                              : item.status === "Low Stock"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ClientPagination pagination={pagination} label="inventory rows" />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
